/**
 * Générateur — Il utilise les carrés parfaits de 1 à 144.
 * Attendu ID : 139 | Niveau : 4ème | Domaine : Nombres (desc 11, O4)
 *
 * Source doc prof (Cluster 1, O4) : calcul de n², reconnaître les carrés parfaits,
 * piège du signe (−5)² vs −5², chiffre des unités d'un carré parfait. Plan de
 * cartes validé 2026-07-14, revu 2026-09-22 :
 *
 *   Carte « carre » — carré d'un entier de 1 à 12 (texte/saisie), trois
 *     formulations tirées au sort.
 *   Carte « reconnaitre » — lesquels sont des carrés parfaits ? (qcm_multiple)
 *   Carte « signe » — (−a)² vs −a² (texte/saisie) — piège du signe
 *   Carte « unites » — parmi les carrés parfaits de 1 à 144, l'un finit-il par
 *     ce chiffre ? (tableau_vf) Vrai ssi le chiffre ∈ {0, 1, 4, 5, 6, 9}.
 *
 * Tout par construction (carrés exacts, prédicat estCarreParfait).
 *
 * Revue 2026-09-22 : carte « carre », le sens inverse « 49 = …² » est RETIRÉ
 * (−7 convenait aussi et était compté faux, alors que la carte « signe » enseigne
 * justement que (−7)² = 49 ; et ce sens faisait doublon avec la carte « valeur »
 * de l'attendu 140, celui de la notation). Les parenthèses explicatives des
 * cartes « signe » et « unites » deviennent des phrases. Aucune carte supprimée.
 *
 * Revue 2026-09-23, PÉRIMÈTRE : la carte « carre » avait été élargie la veille
 * aux décimaux en dixièmes et aux fractions irréductibles. C'est REVENU aux
 * seuls ENTIERS de 1 à 12, décision du professeur, pour trois raisons qui vont
 * dans le même sens. Un carré parfait est un entier par définition, et 9/16
 * n'en est pas un. L'exemple de réussite de cet attendu est « il connaît les
 * égalités du type 11² = 121 et √81 = 9 », que des entiers, et le programme en
 * vigueur dit « les carrés parfaits des ENTIERS de 0 à 12 ». Enfin l'exemple de
 * réussite de l'attendu 201, en 3e, est précisément « 0,3 × 0,3 × 0,3 × 0,3 ;
 * 1/(6 × 6 × 6 × 6) » : le décimal et la fraction élevés à une puissance sont
 * étudiés en 3e. Douze valeurs seulement, donc la variété passe par trois
 * FORMULATIONS tirées au sort (motif de l'attendu 140).
 *
 * Revue 2026-09-23 : typographie. Tout carré AFFICHÉ passe par les helpers
 * KaTeX de generators/utils.js (puissTex, tex) : plus de <sup>2</sup> ni de
 * « ² » écrit à la main. La carte « signe » affiche tex('(-7)^2') et
 * tex('{-7^2}'), les deux écritures que l'élève doit distinguer, la seconde
 * groupée sinon KaTeX espace le moins. Les réponses saisies restent du texte
 * brut (« 121 », « -49 »).
 *
 * Portée 2026-09-23 (question prof « comment ils savent ça, eux ? ») : la carte
 * « unites » affirmait « un carré parfait PEUT se terminer par le chiffre 8 »,
 * c'est-à-dire quelque chose sur TOUS les carrés parfaits. Or l'élève n'en
 * connaît que douze : constater qu'aucun des douze ne finit par 8 ne prouve pas
 * qu'il n'en existe aucun plus loin. La preuve tient au fait que le dernier
 * chiffre d'un carré ne dépend que du dernier chiffre du nombre, dix cas qui
 * donnent 0, 1, 4, 9, 6, 5, 6, 9, 4, 1 ; mais ce raisonnement n'est ni dans
 * l'intitulé, ni dans les exemples de réussite, ni dans le cours de 4e.
 * La phrase porte donc sur les DOUZE carrés de la liste, ce que l'élève vérifie
 * en la récitant : 1, 4, 9, 16, 25, 36, 49, 64, 81, 100, 121, 144, dont les
 * derniers chiffres sont 1, 4, 9, 6, 5, 6, 9, 4, 1, 0, 1, 4. Les six chiffres
 * possibles et les quatre impossibles sont les mêmes que dans le cas général,
 * les valeurs de vérité ne changent pas.
 *
 * Composition 2026-09-23 (règle prof, tableaux vrai-faux) : « unites » passe
 * de six lignes à quatre, sinon c'est trop long à lire. Les chiffres sont
 * tirés par classe, un à trois possibles et le reste impossible : jusqu'ici
 * six chiffres au hasard parmi dix donnaient un tableau tout « vrai » une fois
 * sur deux cents, quatre au hasard l'auraient donné une fois sur quatorze.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, tex, puissTex,
} from '../../../utils';
import { CARRES, estCarreParfait } from './nombres';

const ATTENDU_ID = 139;

const L_CARRE = 'Calculer un carré';
const L_RECONNAITRE = 'Reconnaître les carrés parfaits';
const L_SIGNE = 'Gérer le signe d\'un carré';
const L_UNITES = 'Connaître le chiffre des unités d\'un carré';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Chiffres des unités possibles pour un carré parfait.
const UNITES_POSSIBLES = new Set([0, 1, 4, 5, 6, 9]);
// Nombres NON carrés parfaits « pièges » (proches de carrés / d'apparence).
const NON_CARRES = [8, 18, 24, 27, 44, 48, 50, 63, 72, 90, 99, 110, 122, 125, 128, 140];

// ═════════ Carte « carre » — carré d'un entier de 1 à 12 ═════════

// UNIQUEMENT des entiers (revue 2026-09-23, voir l'en-tête) : un carré parfait
// est un entier, et le décimal comme la fraction élevés à une puissance sont un
// exemple de réussite de 3e. Douze valeurs seulement : la variété vient des
// FORMULATIONS, motif de l'attendu 140 (« 11 valeurs × 2 formulations »).
const CARRE_TEMPLATES = [
    (n) => `<p><b>Calcule le carré de ${n}, c'est-à-dire ${puissTex(n, 2)}.</b></p>`,
    (n) => `<p><b>Combien vaut ${puissTex(n, 2)} ?</b></p>`,
    (n) => `<p>Un carré a un côté de ${n} unités.</p><p><b>Quelle est son aire, en unités d'aire ?</b></p>`,
];

const genCarreSaisie = () => {
    const n = randInt(1, 12);
    const texte = randomChoice(CARRE_TEMPLATES)(n);
    const payload = makeSaisie(String(CARRES[n]), texte, opts(L_CARRE));
    payload._meta.params = { n };
    return payload;
};

// ═════════ Carte « reconnaitre » — lesquels sont des carrés parfaits ? ═════════

const genReconnaitreQcmMultiple = () => {
    const carres = Object.values(CARRES);
    for (let essai = 0; essai < 60; essai++) {
        const bons = shuffle(carres).slice(0, randInt(2, 3));
        const faux = shuffle(NON_CARRES).slice(0, 6 - bons.length);
        const nombres = [...bons, ...faux];
        if (new Set(nombres).size !== nombres.length) continue;
        const choix = shuffle(nombres.map(String));
        const texte = '<p><b>Sélectionne tous les nombres qui sont des carrés parfaits.</b></p>';
        const payload = makeQcm(choix, bons.map(String), texte, opts(L_RECONNAITRE), true);
        payload._meta.params = { bons };
        return payload;
    }
    // Repli déterministe
    const nombres = [16, 81, 44, 99, 110, 125];
    const bons = nombres.filter(estCarreParfait);
    const payload = makeQcm(shuffle(nombres.map(String)), bons.map(String),
        '<p><b>Sélectionne tous les nombres qui sont des carrés parfaits.</b></p>', opts(L_RECONNAITRE), true);
    payload._meta.params = { bons };
    return payload;
};

// ═════════ Carte « signe » — (−a)² vs −a² ═════════

const genSigneSaisie = () => {
    const a = randInt(2, 12);
    const parentheses = randomChoice([true, false]);
    // Les deux écritures que la carte oppose : (-7)^2 et -7^2, en KaTeX.
    // Les accolades de `{-7^2}` ne sont pas décoratives : sans elles, le
    // `\mathrm{}` que `tex()` insère en tête fait du moins un opérateur BINAIRE
    // et KaTeX l'espace des deux côtés (« − 7² »), alors que `(-7)^2` se rend
    // serré. Or c'est justement sur cette carte que les deux écritures se
    // comparent : le groupe rend le moins unaire et l'écart disparaît.
    const expr = parentheses ? tex(`(-${a})^2`) : tex(`{-${a}^2}`);
    const rep = parentheses ? a * a : -(a * a);
    const texte = `<p><b>Calcule ${expr}.</b></p>`
        + '<p>Fais attention aux parenthèses et au signe. Un résultat négatif s\'écrit avec le signe « - ».</p>';
    const payload = makeSaisie(String(rep), texte, opts(L_SIGNE));
    payload._meta.params = { a, parentheses, rep };
    return payload;
};

// ═════════ Carte « unites » — chiffre des unités d'un carré parfait ═════════

const CHIFFRES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
const CHIFFRES_POSSIBLES = CHIFFRES.filter(c => UNITES_POSSIBLES.has(c));         // 0, 1, 4, 5, 6, 9
const CHIFFRES_IMPOSSIBLES = CHIFFRES.filter(c => !UNITES_POSSIBLES.has(c));      // 2, 3, 7, 8
// Quatre lignes (règle prof 2026-09-23 : quatre au plus, sinon trop long à lire).
const NB_LIGNES_UNITES = 4;

const genUnitesTableau = () => {
    // Un à trois chiffres possibles, le reste impossible : les deux réponses
    // sont toujours présentes par construction. Quatre chiffres tirés au hasard
    // parmi les dix donneraient un tableau tout « vrai » une fois sur quatorze
    // (six chiffres possibles sur dix), le tirage est donc par classe :
    // 6 × 4 + 15 × 6 + 20 × 4 = 194 tableaux distincts.
    const nbPossibles = randInt(1, NB_LIGNES_UNITES - 1);
    const chiffres = shuffle([
        ...shuffle(CHIFFRES_POSSIBLES).slice(0, nbPossibles),
        ...shuffle(CHIFFRES_IMPOSSIBLES).slice(0, NB_LIGNES_UNITES - nbPossibles),
    ]);
    // La phrase porte sur les DOUZE carrés que l'élève connaît, pas sur tous
    // les carrés parfaits (revue 2026-09-23, voir l'en-tête) : « l'un d'eux se
    // termine par 8 » se vérifie en récitant la liste, tandis que « un carré
    // parfait peut se terminer par 8 » exigerait la démonstration sur les dix
    // derniers chiffres, qui n'est enseignée nulle part en 4e.
    const lignes = chiffres.map(c => `L'un d'eux se termine par le chiffre ${c}.`);
    const bonnes = chiffres.map(c => (UNITES_POSSIBLES.has(c) ? 0 : 1));
    const payload = makeVraiFaux(lignes, bonnes,
        '<p>On ne regarde que les carrés parfaits de 1 à 144.</p><p><b>Vrai ou faux ?</b></p>', opts(L_UNITES));
    payload._meta.params = { chiffres };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    carre_texte: genCarreSaisie,
    reconnaitre_qcm_multiple: genReconnaitreQcmMultiple,
    signe_texte: genSigneSaisie,
    unites_tableau_vf: genUnitesTableau,
};

const FORMAT_LABELS = {
    texte: 'Saisie', qcm_multiple: 'QCM Multi', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'carre', label: L_CARRE, formatsList: buildFormats('carre') },
    { id: 'reconnaitre', label: L_RECONNAITRE, formatsList: buildFormats('reconnaitre') },
    { id: 'signe', label: L_SIGNE, formatsList: buildFormats('signe') },
    { id: 'unites', label: L_UNITES, formatsList: buildFormats('unites') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
